import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Typography,
  Skeleton,
  Select,
  MenuItem as SelectMenuItem,
  Pagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import InstituteViewModal from "./InstituteViewModal";

const InstituteTable = ({
  institutes,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onUpdate,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedInstituteId, setSelectedInstituteId] = useState(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleMenuClick = (event, institute) => {
    setAnchorEl(event.currentTarget);
    setSelectedInstitute(institute);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInstitute(null);
  };

  const handleView = () => {
    setSelectedInstituteId(selectedInstitute.institue_id);
    setViewModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log("Delete institute:", selectedInstitute);
    // Implement delete functionality here
    handleMenuClose();
  };

  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setSelectedInstituteId(null);
  };

  const handleInstituteUpdate = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (event, value) => {
    onPageChange(event, value - 1);
  };

  const handlePageSizeChange = (event) => {
    onPageSizeChange(event);
  };

  // Skeleton row component
  const SkeletonRow = () => (
    <TableRow>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="80%" height={20} />
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="rounded" width={80} height={24} />
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="60%" height={20} />
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="70%" height={20} />
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="50%" height={20} />
      </TableCell>
      <TableCell align="center" sx={{ py: 2 }}>
        <Skeleton
          variant="circular"
          width={32}
          height={32}
          sx={{ mx: "auto" }}
        />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Paper
        sx={{
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          border: "1px solid #E5E7EB",
          borderRadius: "6px",
        }}
      >
        {/* Header with Search */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#1F1F1F",
            }}
          >
            All Institutes ({totalCount})
          </Typography>

          <TextField
            size="small"
            placeholder="Search"
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              width: "280px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#F9FAFB",
                fontSize: "13px",
                "& fieldset": {
                  borderColor: "#E5E7EB",
                },
                "&:hover fieldset": {
                  borderColor: "#D1D5DB",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#3B82F6",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Table */}
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#FAFAFA",
                  "& th": {
                    borderBottom: "1px solid #E5E7EB",
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 1.5,
                  }}
                >
                  Institute Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 1.5,
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 1.5,
                  }}
                >
                  City
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 1.5,
                  }}
                >
                  State
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 1.5,
                  }}
                >
                  Country
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "center",
                    py: 1.5,
                    width: "80px",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Show skeleton rows when loading
                Array.from(new Array(pageSize)).map((_, index) => (
                  <SkeletonRow key={index} />
                ))
              ) : institutes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography
                      color="text.secondary"
                      fontSize="13px"
                      sx={{ fontWeight: 400 }}
                    >
                      No institutes found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                institutes.map((institute) => (
                  <TableRow
                    key={institute.institue_id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                      "& td": {
                        borderBottom: "1px solid #F3F4F6",
                      },
                      "&:last-child td": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#1F1F1F",
                        fontWeight: 500,
                        py: 2,
                      }}
                    >
                      {institute.institute_name}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={institute.institute_type}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          height: "24px",
                          fontWeight: 500,
                          backgroundColor:
                            institute.institute_type === "Engineering"
                              ? "#DBEAFE"
                              : institute.institute_type === "University"
                              ? "#E0E7FF"
                              : institute.institute_type === "Degree"
                              ? "#DCFCE7"
                              : "#FEF3C7",
                          color:
                            institute.institute_type === "Engineering"
                              ? "#1E40AF"
                              : institute.institute_type === "University"
                              ? "#4338CA"
                              : institute.institute_type === "Degree"
                              ? "#15803D"
                              : "#A16207",
                          border: "none",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        py: 2,
                      }}
                    >
                      {institute.city_name || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        py: 2,
                      }}
                    >
                      {institute.state_name || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        py: 2,
                      }}
                    >
                      {institute.country_name || "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, institute)}
                        sx={{
                          color: "#6B7280",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Custom Pagination Footer */}
        <Box
          sx={{
            borderTop: "1px solid #E5E7EB",
            px: 2,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left side - Rows per page */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Rows per page:
            </Typography>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              sx={{
                fontSize: "13px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#3B82F6",
                },
                "& .MuiSelect-select": {
                  py: 0.5,
                  pr: 3,
                },
              }}
            >
              <SelectMenuItem value={5} sx={{ fontSize: "13px" }}>
                5
              </SelectMenuItem>
              <SelectMenuItem value={10} sx={{ fontSize: "13px" }}>
                10
              </SelectMenuItem>
              <SelectMenuItem value={20} sx={{ fontSize: "13px" }}>
                20
              </SelectMenuItem>
              <SelectMenuItem value={50} sx={{ fontSize: "13px" }}>
                50
              </SelectMenuItem>
            </Select>
          </Box>

          {/* Center - Page info */}
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
            {totalCount === 0
              ? "0 of 0"
              : `${page * pageSize + 1}-${Math.min(
                  (page + 1) * pageSize,
                  totalCount
                )} of ${totalCount}`}
          </Typography>

          {/* Right side - Page numbers */}
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            shape="rounded"
            size="small"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: "13px",
                minWidth: "32px",
                height: "32px",
                color: "#6B7280",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                },
                "&.Mui-selected": {
                  backgroundColor: "#3B82F6",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                },
              },
              "& .MuiPaginationItem-previousNext": {
                color: "#6B7280",
              },
            }}
          />
        </Box>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: "160px",
              mt: 0.5,
              "& .MuiMenuItem-root": {
                fontSize: "13px",
                py: 1,
              },
            },
          }}
        >
          <MenuItem onClick={handleView}>
            <VisibilityIcon sx={{ fontSize: 18, mr: 1.5, color: "#6B7280" }} />
            View
          </MenuItem>
          <MenuItem
            onClick={handleDelete}
            sx={{
              color: "#DC2626 !important",
            }}
          >
            <DeleteIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Delete
          </MenuItem>
        </Menu>
      </Paper>

      {/* View Modal */}
      <InstituteViewModal
        open={viewModalOpen}
        onClose={handleViewModalClose}
        instituteId={selectedInstituteId}
        onUpdate={handleInstituteUpdate}
      />
    </>
  );
};

export default InstituteTable;
